import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { askAiChat } from "@/lib/ai-chat/service";
import { useAuth } from "@/contexts/AuthContext";

type ChatMessage = {
  id: string;
  from: "agent" | "user";
  text: string;
};

const starterMessages: ChatMessage[] = [];
const AI_CHAT_HISTORY_LIMIT = 10;

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1" aria-label="AI is typing" role="status">
      <span className="sr-only">AI is typing</span>
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className="h-2 w-2 rounded-full bg-current/70 animate-bounce"
          style={{ animationDelay: `${dot * 0.15}s` }}
        />
      ))}
    </div>
  );
}

export function AIAgentChat() {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>(starterMessages);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openChat = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setPanelVisible(true);
    setIsClosing(false);
    setOpen(true);
  };

  const closeChat = useCallback(() => {
    if (!panelVisible || isClosing) return;

    setOpen(false);
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setPanelVisible(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, 180);
  }, [isClosing, panelVisible]);

  useEffect(() => {
    if (!panelVisible) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeChat();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [closeChat, panelVisible]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isThinking, messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isThinking) return;

    const userMessage: ChatMessage = { id: `${Date.now()}-user`, from: "user", text: trimmed };
    const role = profile?.role === "agent" || profile?.role === "lawyer" || profile?.role === "client"
      ? profile.role
      : "guest";

    setMessages((current) => [...current, userMessage]);
    setInput("");
    setIsThinking(true);

    try {
      const reply = await askAiChat({
        question: trimmed,
        role,
        history: [...messages, userMessage].slice(-AI_CHAT_HISTORY_LIMIT).map((message) => ({
          from: message.from,
          text: message.text,
        })),
      });

      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-agent`,
          from: "agent",
          text: reply.text,
        },
      ]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "I'm having trouble connecting right now. Please try again.";
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-agent-error`,
          from: "agent",
          text: errorMessage,
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <div ref={containerRef} className="fixed bottom-5 right-5 z-[80]">
      {panelVisible ? (
        <div
          className={`mb-3 flex h-[min(40rem,calc(100vh-6rem))] w-[min(28rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevated duration-200 ${
            open && !isClosing
              ? "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4"
              : "animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-4"
          }`}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">PropTRUST Navigator</p>
                <p className="text-xs text-muted-foreground">Your conversational AI assistant</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={closeChat} aria-label="Close AI chat">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[90%] whitespace-pre-line break-words rounded-lg px-3 py-2 text-sm leading-6 ${
                    message.from === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground"
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
            {isThinking ? (
              <div className="flex justify-start">
                <div className="max-w-[90%] rounded-lg bg-secondary px-3 py-2 text-sm leading-6 text-secondary-foreground">
                  <TypingIndicator />
                </div>
              </div>
            ) : null}
            <div ref={messagesEndRef} />
          </div>
          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSend();
                }}
                placeholder="Ask me anything..."
                disabled={isThinking}
              />
              <Button size="icon" onClick={() => void handleSend()} aria-label="Send AI chat message" disabled={isThinking}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Button
        size="icon"
        className="h-14 w-14 rounded-lg shadow-elevated"
        onClick={panelVisible && !isClosing ? closeChat : openChat}
        aria-label="Open PropTRUST AI chat"
      >
        <Bot className="h-6 w-6" />
      </Button>
    </div>
  );
}
